import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import FileUpload from "components/FileUpload";
import DocumentViewerModal from "components/DocumentViewer";
import { CaseCreation, Execution, ExecutionType, FILE_URL } from "types";
import { DateView, errorHandlerOnForm } from "utils/helper";
import { addExecution, fetchExecutions } from "services/courtCaseService";

type Props = {
  caseData: CaseCreation;
  isProsecutor?: boolean;
};

const ExecutionTab: React.FC<Props> = ({ caseData, isProsecutor }) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const [executions, setExecutions] = useState<Execution[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [executionType, setExecutionType] = useState("imprisonment");
  const [courtDecision, setCourtDecision] = useState("");
  const [amount, setAmount] = useState("");
  const [executionFiles, setExecutionFiles] = useState<File[]>([]);
  const [decisionFile, setDecisionFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const baseUrl = `${process.env.REACT_APP_API_BASE_URL}${FILE_URL.EXECUTION}`;

  const load = () => {
    if (!caseData?.id) return;
    setLoading(true);
    fetchExecutions({ case_id: String(caseData.id) })
      .then((data) => setExecutions(Array.isArray(data) ? data : []))
      .catch(() => setExecutions([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData?.id]);

  const handleCreate = () => {
    if (!courtDecision.trim()) {
      showTooltip(
        t("court_decision_required", {
          defaultValue: "Court decision is required",
        }),
        "error",
      );
      return;
    }
    if (executionFiles.length === 0) {
      showTooltip(
        t("execution_file_required", {
          defaultValue: "At least one execution file is required",
        }),
        "error",
      );
      return;
    }
    setSubmitting(true);
    addExecution({
      case: String(caseData?.id),
      execution_type: executionType,
      court_decision: courtDecision,
      court_decision_amount: amount,
      execution_file: executionFiles, // multiple — repeated key
      court_decision_file: decisionFile,
      admin_unit_id: loginData?.admin_unit ? String(loginData.admin_unit) : null,
    })
      .then(() => {
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        setShowForm(false);
        setExecutionType("imprisonment");
        setCourtDecision("");
        setAmount("");
        setExecutionFiles([]);
        setDecisionFile(null);
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-2 h-8 bg-red-600 rounded-full"></div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t("execution", { defaultValue: "Execution" })}
          </h3>
        </div>
        {isProsecutor && (
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="px-5 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            + {t("add_execution", { defaultValue: "Add Execution" })}
          </button>
        )}
      </div>

      {showForm && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1">
                {t("execution_type", { defaultValue: "Execution Type" })}
              </label>
              <select
                value={executionType}
                onChange={(e) => setExecutionType(e.target.value)}
                className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              >
                {Object.entries(ExecutionType).map(([key, label]) => (
                  <option key={key} value={key}>
                    {t(`execution_type_${key}`, { defaultValue: label })}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1">
                {t("court_decision_amount", {
                  defaultValue: "Decision Amount",
                })}
              </label>
              <input
                type="text"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              />
            </div>
            <div className="flex flex-col md:col-span-2">
              <label className="text-sm text-gray-600 mb-1">
                {t("court_decision", { defaultValue: "Court Decision" })}
                <span className="text-red-500"> *</span>
              </label>
              <textarea
                value={courtDecision}
                onChange={(e) => setCourtDecision(e.target.value)}
                rows={3}
                className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              />
            </div>
            <FileUpload
              label={t("execution_file", {
                defaultValue: "Execution File(s)",
              })}
              required
              multiple
              fileTypes="*"
              onChange={(file) =>
                setExecutionFiles((prev) =>
                  file ? [...prev, file] : prev,
                )
              }
            />
            <FileUpload
              label={t("court_decision_file", {
                defaultValue: "Court Decision File",
              })}
              fileTypes="*"
              onChange={setDecisionFile}
            />
          </div>
          {executionFiles.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {executionFiles.map((f, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-gray-300 rounded-full text-xs"
                >
                  {f.name}
                  <button
                    type="button"
                    onClick={() =>
                      setExecutionFiles((prev) =>
                        prev.filter((_, idx) => idx !== i),
                      )
                    }
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={handleCreate}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60"
            >
              {submitting
                ? t("loading", { defaultValue: "Loading..." })
                : t("submit", { defaultValue: "Submit" })}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">
          {t("loading", { defaultValue: "Loading..." })}
        </div>
      ) : executions.length === 0 ? (
        <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-lg">
          {t("no_executions", { defaultValue: "No executions yet" })}
        </div>
      ) : (
        <div className="space-y-4">
          {executions.map((ex) => (
            <div
              key={ex.id}
              className="border border-gray-200 rounded-xl p-5 bg-white"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <h4 className="text-base font-semibold text-gray-900">
                    {t(`execution_type_${ex.execution_type}`, {
                      defaultValue:
                        ExecutionType[
                          ex.execution_type as keyof typeof ExecutionType
                        ] || ex.execution_type,
                    })}
                  </h4>
                  {ex.court_decision && (
                    <p className="text-sm text-gray-600 whitespace-pre-line">
                      {ex.court_decision}
                    </p>
                  )}
                  {ex.court_decision_amount ? (
                    <p className="text-xs text-gray-500">
                      {t("court_decision_amount", {
                        defaultValue: "Decision Amount",
                      })}
                      : {ex.court_decision_amount}
                    </p>
                  ) : null}
                  {ex.created_at && (
                    <p className="text-xs text-gray-400">
                      {DateView(ex.created_at)}
                    </p>
                  )}
                </div>
              </div>

              {Array.isArray(ex.documents) && ex.documents.length > 0 && (
                <div className="mt-4">
                  <DocumentViewerModal
                    documents={ex.documents as any}
                    baseUrl={baseUrl}
                    layout="list"
                    hideEmptyFile
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExecutionTab;
